/**
 * Request and response shapes of the mai-work-clock web API, shared by the server (validation)
 * and the web app (forms and response parsing). Instants are ISO 8601 strings in UTC; local
 * dates are `YYYY-MM-DD`; schedule times are minutes after local midnight.
 *
 * Geofence data (results, distances, fence names, IP addresses) is only ever sent in the
 * dedicated supervisor fields (`evidence`, `outsideGeofenceDates`, `outsideGeofence`), which are
 * `null` for employees.
 */
import { ClockState, GeofenceResult, MAX_SESSION_HOURS, ModificationStatus, PunchType, Weekday, WorkDayStatus, zIsoDate, } from '@mai-team-amusement/core';
import { z } from 'zod';
export const MINUTES_PER_HOUR = 60;
export const HOURS_PER_DAY = 24;
export const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;
export const MAX_SESSION_MINUTES = MAX_SESSION_HOURS * MINUTES_PER_HOUR;
export const REASON_MAX_LENGTH = 500;
export const COMMENT_MAX_LENGTH = 500;
export const MAX_BREAKS_PER_DAY = 20;
export const GEOFENCE_NAME_MAX_LENGTH = 100;
export const GEOFENCE_RADIUS_MIN_M = 10;
export const GEOFENCE_RADIUS_MAX_M = 10_000;
export const DEFAULT_AUDIT_PAGE_SIZE = 50;
export const MAX_AUDIT_PAGE_SIZE = 200;
const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;
/** Accuracy reports above this (meters) are not plausible GPS/Wi-Fi positions. */
const MAX_ACCURACY_M = 100_000;
const MAX_BREAK_MINUTES = 720;
const MIN_YEAR = 2000;
const MAX_YEAR = 2999;
const zInstant = z.iso.datetime();
const zSub = z.uuid();
/** `YYYY-MM`. */
export const zMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const zMinuteOfDay = z
    .number()
    .int()
    .min(0)
    .max(MINUTES_PER_DAY - 1);
// ---------------------------------------------------------------------------------------------
// Clock
export const zPosition = z.object({
    latitude: z.number().min(-MAX_LATITUDE).max(MAX_LATITUDE),
    longitude: z.number().min(-MAX_LONGITUDE).max(MAX_LONGITUDE),
    accuracyM: z.number().min(0).max(MAX_ACCURACY_M),
});
/** `position` is checked separately so a missing one yields `PositionRequired`. */
export const zPunchInput = z.object({
    type: z.enum(PunchType),
    position: zPosition.nullable().optional(),
});
export const zBreakView = z.object({
    startAt: zInstant,
    endAt: zInstant.nullable(),
});
export const zWorkDayView = z.object({
    id: z.number().int(),
    workDate: zIsoDate,
    timeZone: z.string(),
    startAt: zInstant,
    endAt: zInstant.nullable(),
    status: z.enum(WorkDayStatus),
    isModified: z.boolean(),
    /** Ended automatically MAX_SESSION_HOURS after the start ("end work" was never pressed). */
    autoEnded: z.boolean(),
    version: z.number().int(),
    breaks: z.array(zBreakView),
});
/** A punch as its owner sees it: what and when, and where it was recorded. */
export const zPunchView = z.object({
    id: z.number().int(),
    type: z.enum(PunchType),
    occurredAt: zInstant,
    latitude: z.number(),
    longitude: z.number(),
});
/** Supervisor-only details of a punch (group managers and admins). */
export const zPunchEvidence = z.object({
    punchId: z.number().int(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    accuracyM: z.number(),
    geofenceResult: z.enum(GeofenceResult),
    geofenceName: z.string().nullable(),
    distanceM: z.number().int().nullable(),
});
export const zClockStatus = z.object({
    /** Server time (the client shows it with its own clock's offset corrected). */
    now: zInstant,
    timeZone: z.string(),
    state: z.enum(ClockState),
    /**
     * The open work day, else today's day, else the latest day while its end can still be updated
     * (an overnight session the next morning), else `null`.
     */
    workDay: zWorkDayView.nullable(),
    punches: z.array(zPunchView),
    /**
     * The punches the user can make now. Off duty, "end work" is offered again for the latest day
     * (the end time moves to the new press) until the next start, within MAX_SESSION_HOURS.
     */
    allowedPunches: z.array(z.enum(PunchType)),
    /** Work dates whose session ended automatically and should be corrected by a request. */
    needsCorrection: z.array(zIsoDate),
});
// ---------------------------------------------------------------------------------------------
// People
export const zMemberView = z.object({
    sub: z.string(),
    familyName: z.string(),
    givenName: z.string(),
    employeeNumber: z.string(),
    groupId: z.number().int(),
    groupName: z.string().nullable(),
    timeZone: z.string(),
});
export const zGroupOption = z.object({ id: z.number().int(), name: z.string() });
export const zGroupList = z.object({ groups: z.array(zGroupOption) });
// ---------------------------------------------------------------------------------------------
// Schedules
export const zScheduleDay = z.object({
    weekday: z.enum(Weekday),
    isWorkday: z.boolean(),
    /** Start and end in minutes after local midnight; `end <= start` means the shift ends the next day. */
    startMinute: zMinuteOfDay,
    endMinute: zMinuteOfDay,
    breakMinutes: z.number().int().min(0).max(MAX_BREAK_MINUTES),
});
/** Shift length in minutes (overnight-aware; equal start and end is a 24-hour shift). */
export function shiftMinutes(day) {
    const length = day.endMinute - day.startMinute;
    return length > 0 ? length : length + MINUTES_PER_DAY;
}
const zScheduleTarget = z.union([
    z.object({ groupId: z.number().int().positive() }),
    z.object({ userSub: zSub }),
]);
export const zScheduleInput = z
    .object({
    target: zScheduleTarget,
    effectiveFrom: zIsoDate,
    days: z.array(zScheduleDay).length(Object.keys(Weekday).length),
})
    .refine((input) => new Set(input.days.map((day) => day.weekday)).size === input.days.length, {
    message: 'each weekday once',
    path: ['days'],
})
    .refine((input) => input.days.every((day) => !day.isWorkday || day.breakMinutes < shiftMinutes(day)), { message: 'breaks must be shorter than the shift', path: ['days'] });
export const zScheduleQuery = z.union([
    z.object({ groupId: z.coerce.number().int().positive() }),
    z.object({ userSub: zSub }),
]);
export const zScheduleView = z.object({
    id: z.number().int(),
    groupId: z.number().int().nullable(),
    userSub: z.string().nullable(),
    effectiveFrom: zIsoDate,
    createdBy: z.string().nullable(),
    createdAt: zInstant,
    days: z.array(zScheduleDay),
});
export const zScheduleList = z.object({ schedules: z.array(zScheduleView) });
// ---------------------------------------------------------------------------------------------
// Records
export const zDayMetrics = z.object({
    /** Planned working minutes (shift minus breaks); `null` without a schedule or on days off. */
    scheduledMinutes: z.number().int().nullable(),
    /** `null` while the session is open or when there is no record. */
    workedMinutes: z.number().int().nullable(),
    breakMinutes: z.number().int(),
    lateMinutes: z.number().int(),
    earlyLeaveMinutes: z.number().int(),
    overtimeMinutes: z.number().int(),
    holidayWorkMinutes: z.number().int(),
});
export const zRecordRow = z.object({
    date: zIsoDate,
    weekday: z.enum(Weekday),
    schedule: zScheduleDay.nullable(),
    workDay: zWorkDayView.nullable(),
    metrics: zDayMetrics,
    /** A past scheduled workday without a record, or a session that ended automatically. */
    missingPunch: z.boolean(),
    pendingRequest: z.boolean(),
    /** "End work" was pressed more than once; the end is the latest press. */
    endUpdated: z.boolean(),
});
export const zRecordTotals = z.object({
    scheduledWorkdays: z.number().int(),
    daysWorked: z.number().int(),
    workedMinutes: z.number().int(),
    breakMinutes: z.number().int(),
    overtimeMinutes: z.number().int(),
    holidayWorkMinutes: z.number().int(),
    lateCount: z.number().int(),
    earlyLeaveCount: z.number().int(),
    missingPunchCount: z.number().int(),
    modifiedDays: z.number().int(),
    endUpdatedDays: z.number().int(),
    autoEndedDays: z.number().int(),
});
export const zMonthQuery = z.object({ month: zMonth });
export const zYearQuery = z.object({
    year: z.coerce.number().int().min(MIN_YEAR).max(MAX_YEAR),
});
export const zMonthRecords = z.object({
    user: zMemberView,
    month: zMonth,
    rows: z.array(zRecordRow),
    totals: zRecordTotals,
    /** Supervisors only: dates with a punch outside every geofence. */
    outsideGeofenceDates: z.array(zIsoDate).nullable(),
});
// ---------------------------------------------------------------------------------------------
// Modification requests
export const zSpanInput = z.object({ startAt: zInstant, endAt: zInstant });
export const zModificationInput = z.object({
    workDate: zIsoDate,
    startAt: zInstant,
    endAt: zInstant,
    breaks: z.array(zSpanInput).max(MAX_BREAKS_PER_DAY),
    reason: z.string().trim().min(1).max(REASON_MAX_LENGTH),
});
export const zReviewInput = z.object({
    comment: z.string().trim().max(COMMENT_MAX_LENGTH).optional(),
});
export const ModificationScope = {
    /** The signed-in user's own requests. */
    Mine: 'mine',
    /** Requests the signed-in user may approve. */
    Review: 'review',
};
export const zModificationListQuery = z.object({
    scope: z.enum(ModificationScope).default(ModificationScope.Mine),
    status: z.coerce.number().pipe(z.enum(ModificationStatus)).optional(),
});
export const zReviewer = z.object({ sub: z.string(), name: z.string().nullable() });
export const zModificationView = z.object({
    id: z.number().int(),
    user: zMemberView,
    workDate: zIsoDate,
    timeZone: z.string(),
    requestedStartAt: zInstant,
    requestedEndAt: zInstant,
    breaks: z.array(zSpanInput),
    reason: z.string(),
    status: z.enum(ModificationStatus),
    requestedAt: zInstant,
    reviewer: zReviewer.nullable(),
    reviewedAt: zInstant.nullable(),
    reviewComment: z.string().nullable(),
    /** The day before the change: the current record while pending, the snapshot once approved. */
    before: zWorkDayView.nullable(),
});
export const zModificationList = z.object({ requests: z.array(zModificationView) });
export const zPendingCount = z.object({ count: z.number().int() });
export const zWorkDayDetail = z.object({
    user: zMemberView,
    date: zIsoDate,
    schedule: zScheduleDay.nullable(),
    workDay: zWorkDayView.nullable(),
    metrics: zDayMetrics,
    punches: z.array(zPunchView),
    /** Supervisors only. */
    evidence: z.array(zPunchEvidence).nullable(),
    requests: z.array(zModificationView),
});
// ---------------------------------------------------------------------------------------------
// Team
export const zTeamQuery = z.object({ groupId: z.coerce.number().int().positive().optional() });
export const zTeamMonthQuery = zTeamQuery.extend({ month: zMonth });
export const zTeamTodayMember = z.object({
    user: zMemberView,
    /** The member's local date. */
    date: zIsoDate,
    state: z.enum(ClockState),
    workDay: zWorkDayView.nullable(),
    outsideGeofence: z.boolean(),
    needsCorrection: z.boolean(),
    pendingRequests: z.number().int(),
});
export const zTeamToday = z.object({ members: z.array(zTeamTodayMember) });
export const zTeamMonthMember = z.object({
    user: zMemberView,
    totals: zRecordTotals,
    outsideGeofenceDays: z.number().int(),
    needsCorrectionDays: z.number().int(),
});
export const zTeamMonth = z.object({ month: zMonth, members: z.array(zTeamMonthMember) });
// ---------------------------------------------------------------------------------------------
// Geofences (admins)
export const zGeofenceInput = z.object({
    groupId: z.number().int().positive(),
    name: z.string().trim().min(1).max(GEOFENCE_NAME_MAX_LENGTH),
    latitude: z.number().min(-MAX_LATITUDE).max(MAX_LATITUDE),
    longitude: z.number().min(-MAX_LONGITUDE).max(MAX_LONGITUDE),
    radiusM: z.number().int().min(GEOFENCE_RADIUS_MIN_M).max(GEOFENCE_RADIUS_MAX_M),
    active: z.boolean(),
});
export const zGeofenceUpdate = zGeofenceInput.omit({ groupId: true }).partial();
export const zGeofenceView = zGeofenceInput.extend({ id: z.number().int() });
export const zGeofenceList = z.object({ geofences: z.array(zGeofenceView) });
// ---------------------------------------------------------------------------------------------
// Audit log (admins)
export const zAuditQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_AUDIT_PAGE_SIZE)
        .default(DEFAULT_AUDIT_PAGE_SIZE),
});
export const zAuditEntry = z.object({
    id: z.number().int(),
    actorUserId: z.string().nullable(),
    /** The actor's name from the local directory (`null` for the system or unknown users). */
    actorName: z.string().nullable(),
    action: z.number().int(),
    targetType: z.string(),
    targetId: z.string(),
    before: z.unknown(),
    after: z.unknown(),
    ip: z.string().nullable(),
    createdAt: zInstant,
});
export const zAuditPage = z.object({ items: z.array(zAuditEntry), total: z.number().int() });
// ---------------------------------------------------------------------------------------------
// API paths
export const ClockApiPath = {
    Clock: '/api/v1/clock',
    Punches: '/api/v1/clock/punches',
    Records: '/api/v1/users/:sub/records',
    WorkDay: '/api/v1/users/:sub/work-days/:date',
    MonthlyCsv: '/api/v1/users/:sub/exports/monthly',
    AnnualCsv: '/api/v1/users/:sub/exports/annual',
    Requests: '/api/v1/modification-requests',
    PendingCount: '/api/v1/modification-requests/pending-count',
    Request: '/api/v1/modification-requests/:id',
    Approve: '/api/v1/modification-requests/:id/approve',
    Reject: '/api/v1/modification-requests/:id/reject',
    Cancel: '/api/v1/modification-requests/:id/cancel',
    TeamToday: '/api/v1/team/today',
    TeamMonth: '/api/v1/team/records',
    Groups: '/api/v1/groups',
    Schedules: '/api/v1/schedules',
    Schedule: '/api/v1/schedules/:id',
    Geofences: '/api/v1/geofences',
    Geofence: '/api/v1/geofences/:id',
    AuditLogs: '/api/v1/admin/audit-logs',
};
/** Fills `:name` placeholders of an API path. */
export function apiPath(path, params) {
    return path.replace(/:(\w+)/g, (placeholder, name) => {
        const value = params[name];
        return value === undefined ? placeholder : encodeURIComponent(String(value));
    });
}
//# sourceMappingURL=index.js.map